/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsTheme} from "@exds/theme";
import {ExdsButton, ExdsLink} from '@exds/core';
import {TuxModal} from '@tuxedo/react-component-library';
import React, {useContext, useEffect, useState} from "react";
import {
  HELP_LINK,
  NOT_ALLOWED_TO_ONBOARD_TODAY,
  ONBOARDED_USER,
  VALIDATE_IDENTITY_ERROR_CONTENT
} from "../../../constants/constants";
import {apiPost} from "../../../service/service";
import {generateKeyPair} from "../../E2ee/E2ee";
import AppContext from "../../../AppContext";
import {addExitEvent} from "../../AdobeAnalytics/AdobeAnalytics";

export default function MissingAccessCodeModalInternal({isAccessCodeModalOpen, setIsAccessCodeModalOpen, setError, setPageReloadState, isLoading, setIsLoading, dialCode, pageName, setPageState, pageStates}) {
  const {
    sidContext,
    phoneNumberContext,
    deliveryMethodContext,
    transactionIdContext,
    firstNameContext,
    lastNameContext,
    hashContext,
    expirationTimeContext,
    plainTextPhoneNumberContext,
    isSoftTokenEnabledContext,
    isSmartLogonEnabledContext,
    mppUrlContext,
    loadAA
  } = useContext(AppContext);
  const sid = sidContext.at(0);
  const deliveryMethod = deliveryMethodContext.at(0);
  const setFirstName = firstNameContext.at(1);
  const setLastName = lastNameContext.at(1);
  const setTransactionId = transactionIdContext.at(1);
  const setHash = hashContext.at(1);
  const mppUrl = mppUrlContext.at(0);
  const setExpirationTime = expirationTimeContext.at(1);
  const setIsSoftTokenEnabled = isSoftTokenEnabledContext.at(1);
  const setIsSmartLogonEnabled = isSmartLogonEnabledContext.at(1);
  const plainPhoneNumber = plainTextPhoneNumberContext.at(0);
  const [phoneNumber, setPhoneNumber] = phoneNumberContext;
  const [keyPair, setKeyPair] = useState({});

  useEffect(() => {
    const setKeys = async () => {
      const pair = await generateKeyPair();
      setKeyPair(pair);
    };
    setKeys();
  }, []);

  const handleChangeDetails = () => {
    setIsAccessCodeModalOpen(false);
    setPageReloadState();
  };

  function getErrorDetails(error, correlationId) {
    switch (error) {
      case "NOT_TODAY":
        return {
          correlationId,
          errorMessage: NOT_ALLOWED_TO_ONBOARD_TODAY
        };
      case "ACTIVE":
        return {
          correlationId,
          errorMessage: ONBOARDED_USER,
          buttonText: 'Update my password',
          buttonLink: mppUrl,
          externalRedirectLink: true,
          windowOpenTarget: '_blank',
          windowDimensions: 'width=800,height=600'
        };
      default:
        return {
          correlationId,
          errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT,
          buttonText: 'Try Again',
          buttonLink: '/validateIdentity',
          windowOpenTarget: '_self',
        };
    }
  }

  function handleError(response) {
    const error = response.data.errorCode ;
    const errorDetails = getErrorDetails(error, response.correlationId);
    setError({...error, ...errorDetails});
  }

  const handleResendCode = async () => {
      setIsLoading(true);
      setIsAccessCodeModalOpen(false);
      apiPost('/api/validateUser', {
        sid,
        phoneNumber,
        deliveryMethod,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }, {'PUBLIC_KEY': keyPair.publicKey})
        .then(async (response) => {
          setIsLoading(false);
          if (response.status === 200) {
            if(!response.data.errorCode) {
              setFirstName(response.data.firstName);
              setLastName(response.data.lastName);
              setTransactionId(response.data.transactionId);
              setExpirationTime(response.data.expirationTime);
              setPhoneNumber(response.data.phoneNumber);
              setIsSoftTokenEnabled(response.data.softTokenEnabled);
              setIsSmartLogonEnabled(response.data.smartLogonEnabled);
              setHash(response.data.hash);
            } else {
              handleError(response);
              setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
            }
          } else {
            handleError(response);
            setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
          }
        })
        .catch((err) => {
          setError({...err, errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT, correlationId: err?.correlationId,
            buttonText: 'Try Again', buttonLink: '/validateIdentity'});
          setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
        })
        .finally(() => setIsLoading(false));
  };

  const redactPhoneNumber = () => {
    return plainPhoneNumber.replace(/\D/g, '').replace(/\d(?=\d{3})/g, '*');
  };

  return (
    <>
      {!isLoading && <TuxModal
      className="modal-container"
      type='popup'
      title="Haven't received your code?"
      open={isAccessCodeModalOpen}
      onCloseModal={() => setIsAccessCodeModalOpen(false)}
      body={
        <ExdsTheme>
          <p>
            If you still haven't received your code <b>after 5 minutes</b>, make sure you entered the same number
            we have on file from your hiring process and click resend code.
          </p>
          <p>
            Number entered: <b>{redactPhoneNumber()}</b>
          </p>
          <p>
            If you're still having trouble contact&nbsp;
            <ExdsLink
              href={HELP_LINK}
              textDecoration={['underline']}
              className="fw-400"
              target="_blank"
              onClick={() => {
                if(loadAA) {
                  addExitEvent(pageName, sid, HELP_LINK, 'Link', 'Ask JPMC', 'Haven\'t received your code modal');
                }
              }}>
              Ask JPMC
            </ExdsLink>
          </p>
        </ExdsTheme>
      }
      footer={
        <ExdsTheme>
          <ExdsButton
            text="Change details"
            variant="secondary"
            color='brand'
            onClick={() => handleChangeDetails()}
          />
          <ExdsButton
            text="Resend Code"
            variant="primary"
            onClick={handleResendCode}
          />
        </ExdsTheme>
      }
    />}
    </>
  );
}
